// NominalCodePicker — reusable nominal code multi-select component
//
// Renders a popover (recently-used + search) with a "Show More" full-table dialog.
// Used in CategoryFormDialog to assign nominal codes to budget categories.

import * as React from 'react';
import {
  ArrowRight,
  Clock,
  Search,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { NominalCode } from '@/lib/cost-category-mapping-storage';

// ─────────────────────────────────────────────────────────────────────────────
// Default recently-used lists
// ─────────────────────────────────────────────────────────────────────────────

export const COST_RECENTLY_USED  = ['4000', '4010', '5000', '5200', '5300', '6000'];
export const SALES_RECENTLY_USED = ['3000', '3010', '3100', '3200', '3300', '3400'];

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface NominalCodePickerProps {
  allCodes: NominalCode[];
  selected: string[];
  onValueChange: (codes: string[]) => void;
  hasConflict?: boolean;
  recentlyUsedCodes?: string[];
  placeholder?: string;
  dialogTitle?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper
// ─────────────────────────────────────────────────────────────────────────────

function pluralise(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function NominalCodePicker({
  allCodes,
  selected,
  onValueChange,
  hasConflict,
  recentlyUsedCodes = COST_RECENTLY_USED,
  placeholder = 'Assign nominal codes…',
  dialogTitle = 'Select Nominal Code',
}: NominalCodePickerProps) {
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [modalOpen, setModalOpen]     = React.useState(false);
  const [modalSearch, setModalSearch] = React.useState('');

  const recentCodes = React.useMemo(
    () =>
      recentlyUsedCodes
        .map((c) => allCodes.find((n) => n.nominalCode === c))
        .filter((n): n is NominalCode => n !== undefined),
    [allCodes, recentlyUsedCodes],
  );

  const filteredModal = React.useMemo(() => {
    const q = modalSearch.trim().toLowerCase();
    if (!q) return allCodes;
    return allCodes.filter(
      (n) =>
        n.nominalCode.toLowerCase().includes(q) ||
        n.nominalName.toLowerCase().includes(q) ||
        n.debitAccountName.toLowerCase().includes(q) ||
        n.creditAccountName.toLowerCase().includes(q) ||
        n.vatAccountName.toLowerCase().includes(q),
    );
  }, [allCodes, modalSearch]);

  const toggle = (code: string) => {
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];
    onValueChange(next);
  };

  const handleShowMore = () => {
    setPopoverOpen(false);
    setModalOpen(true);
    setModalSearch('');
  };

  const selectedItems = allCodes.filter((n) => selected.includes(n.nominalCode));

  return (
    <>
      {/* ── Trigger: selected badges + open-popover button ── */}
      <div className="flex flex-wrap items-center gap-1.5">
        {selectedItems.map((n) => (
          <Badge
            key={n.nominalCode}
            variant="secondary"
            className="gap-1 pr-1 font-mono text-xs"
          >
            {n.nominalCode} — {n.nominalName}
            <button
              type="button"
              onClick={() => toggle(n.nominalCode)}
              className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
              aria-label={`Remove ${n.nominalCode}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}

        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-7 gap-1.5 text-xs',
                hasConflict && 'border-destructive text-destructive hover:border-destructive',
              )}
            >
              <Search className="h-3 w-3" />
              {selected.length === 0 ? placeholder : 'Add more…'}
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-[380px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search by code or name…" />
              <CommandList>
                <CommandEmpty>No nominal codes found.</CommandEmpty>

                {/* Recently used */}
                <CommandGroup heading="Recently Used">
                  {recentCodes.map((n) => (
                    <CommandItem
                      key={n.nominalCode}
                      value={`${n.nominalCode} ${n.nominalName}`}
                      onSelect={() => toggle(n.nominalCode)}
                      className="cursor-pointer gap-2"
                    >
                      <Checkbox
                        checked={selected.includes(n.nominalCode)}
                        className="shrink-0"
                        onCheckedChange={() => toggle(n.nominalCode)}
                      />
                      <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      {n.nominalCode} — {n.nominalName}
                    </CommandItem>
                  ))}
                </CommandGroup>

                {/* Show More */}
                <CommandGroup>
                  <CommandItem
                    onSelect={handleShowMore}
                    className="cursor-pointer justify-center font-medium text-primary gap-2"
                  >
                    <ArrowRight className="h-4 w-4" />
                    Show More…
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* ── Full "Select Nominal Code" modal ── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-[92vw] w-[1200px] max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              {filteredModal.length} nominal {filteredModal.length === 1 ? 'code' : 'codes'} found
            </DialogDescription>
          </DialogHeader>

          {/* Search */}
          <div className="px-6 pt-3 pb-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search nominal codes…"
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[40px]" />
                  <TableHead className="w-[90px]">Code</TableHead>
                  <TableHead className="min-w-[200px]">Description</TableHead>
                  <TableHead className="w-[80px]">Debit</TableHead>
                  <TableHead className="min-w-[160px]">Debit Account Name</TableHead>
                  <TableHead className="w-[80px]">Credit</TableHead>
                  <TableHead className="min-w-[160px]">Credit Account Name</TableHead>
                  <TableHead className="w-[60px]">VAT</TableHead>
                  <TableHead className="min-w-[140px]">VAT Account Name</TableHead>
                  <TableHead className="w-[80px]">Sort Key</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModal.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-10">
                      No nominal codes match your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredModal.map((n) => {
                    const isSelected = selected.includes(n.nominalCode);
                    return (
                      <TableRow
                        key={n.nominalCode}
                        className={cn('cursor-pointer', isSelected && 'bg-primary/5')}
                        onClick={() => toggle(n.nominalCode)}
                        data-state={isSelected ? 'selected' : undefined}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggle(n.nominalCode)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>{n.nominalCode}</TableCell>
                        <TableCell>{n.nominalName}</TableCell>
                        <TableCell>{n.debit}</TableCell>
                        <TableCell>{n.debitAccountName}</TableCell>
                        <TableCell>{n.credit}</TableCell>
                        <TableCell>{n.creditAccountName}</TableCell>
                        <TableCell>{n.vat}</TableCell>
                        <TableCell>{n.vatAccountName}</TableCell>
                        <TableCell>{n.sortKey}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <Label className="text-muted-foreground font-normal">
              {selected.length > 0
                ? `${pluralise(selected.length, 'code')} selected`
                : 'No codes selected'}
            </Label>
            <Button onClick={() => setModalOpen(false)} size="sm">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
